import React, { ReactNode, useEffect } from 'react';
import {
  AdvancedMarker,
  AdvancedMarkerProps,
  APIProvider,
  Map as GMap,
  MapProps,
  useMap,
  useMapsLibrary,
} from '@vis.gl/react-google-maps';

import { brisbaneCenter, presetZoom, RouteData } from './common';

/**
 * Lat/lng location type for map center and markers.
 * @property lat - Latitude
 * @property lng - Longitude
 */
type Location = {
  lat: number;
  lng: number;
};

/**
 * Polyline preview properties for styling.
 * @property strokeColor
 * @property strokeWeight
 * @property strokeOpacity
 */
type RoutePreviewProperties = {
  strokeColor?: string;
  strokeWeight?: number;
  strokeOpacity?: number;
};

/**
 * Props for the main Map component.
 * @property defaultCenter - Default map center
 * @property routeData - Route data for directions/polyline
 * @property routeOptions - Polyline preview options
 */
type CommonProps = {
  onMapReady?: (map: google.maps.Map | null) => void;
  defaultCenter: Location;
  routeData?: RouteData;
  routeOptions?: {
    previewProperties?: RoutePreviewProperties;
  };
} & MapProps;

/**
 * Props for Map with children.
 * @property children
 */
type WithChildrenProps = CommonProps & {
  children: ReactNode;
  apiKey?: string;
};

/**
 * Props for Map without children (with elements).
 * @property elements
 */
type WithoutChildrenProps = CommonProps & {
  children?: never;
  elements: AdvancedMarkerProps[];
  apiKey?: string;
};

/**
 * Union type for Map component props.
 */
type ComponentProps = WithChildrenProps | WithoutChildrenProps;

/**
 * Main map content component for rendering Google Map, polyline, and markers.
 * @param props ComponentProps
 */
const MapContent: React.FC<ComponentProps> = (props) => {
  /**
   * Type guard to check if props include children.
   * @param props
   */
  function hasChildren(props: ComponentProps): props is WithChildrenProps {
    return (props as WithChildrenProps).children !== undefined;
  }

  const {
    children,
    onMapReady,
    routeData,
    defaultCenter,
    defaultZoom,
    center,
    zoom,
    routeOptions,
    apiKey,
    ...restProps
  } = props;
  const elements = hasChildren(props) ? undefined : props.elements || [];
  const map = useMap();
  const geometryLibrary = useMapsLibrary('geometry');
  const mapsLibrary = useMapsLibrary('maps');

  useEffect(() => {
    if (onMapReady && map) {
      onMapReady(map);
    }
  }, [map, onMapReady]);

  useEffect(() => {
    const encodedPolyline = routeData?.polyline;
    if (!map || !geometryLibrary || !mapsLibrary || !encodedPolyline || !apiKey) {
      return;
    }
    let polyline: google.maps.Polyline | null = null;
    try {
      const path = geometryLibrary.encoding.decodePath(encodedPolyline);
      polyline = new mapsLibrary.Polyline({
        path,
        map,
        strokeColor: routeOptions?.previewProperties?.strokeColor,
        strokeWeight: routeOptions?.previewProperties?.strokeWeight,
        strokeOpacity: routeOptions?.previewProperties?.strokeOpacity,
      });
      const bounds = new window.google.maps.LatLngBounds();
      path.forEach((latLng: google.maps.LatLng | { lat: number; lng: number }) => {
        bounds.extend(latLng);
      });
      map.fitBounds(bounds);
    } catch (err) {
      console.error('Error in map polyline effect:', err);
    }
    return () => {
      if (polyline) polyline.setMap(null);
    };
  }, [
    map,
    geometryLibrary,
    mapsLibrary,
    routeData?.polyline,
    routeOptions?.previewProperties?.strokeColor,
    routeOptions?.previewProperties?.strokeWeight,
    routeOptions?.previewProperties?.strokeOpacity,
    apiKey,
  ]);

  /**
   * Ensures required props for GMap (center and zoom).
   */

  const mapCenter = defaultCenter || brisbaneCenter;
  const mapZoom = zoom || defaultZoom || presetZoom;

  /**
   * Renders map markers or children.
   */
  const renderContent = () => {
    if (hasChildren(props)) {
      return children;
    } else if (elements) {
      return elements.map((element, index) => (
        <div key={index}>
          <AdvancedMarker {...element} />
        </div>
      ));
    }
    return null;
  };

  return (
    <GMap {...restProps} center={center} zoom={zoom} defaultCenter={mapCenter} defaultZoom={mapZoom}>
      {apiKey && renderContent()}
    </GMap>
  );
};

/**
 * Main Map wrapper component. Provides Google Maps API context and renders MapContent.
 * @param properties ComponentProps
 */
const MapComponent: React.FC<ComponentProps> = (properties) => {
  const apiKey = process.env.REACT_APP_GOOGLE_MAPS_KEY ?? '';

  return (
    <APIProvider apiKey={apiKey}>
      <MapContent {...properties} apiKey={apiKey} />
    </APIProvider>
  );
};

export default MapComponent;
